import { useTranslation } from 'react-i18next'

import { updateUserProfile } from '../../../../../api'
import { EMPTY_LIST } from '../../../../../constants/common'
import {
  USER_STATUS_CODE_BLOCKED,
  USER_STATUS_CODE_IMPORTED,
} from '../../../../../constants/user'
import { useNotification, useUserData } from '../../../../../hooks'
import { getItemsIds } from '../../../../../utils/common'
import { isStaffUser } from '../../../../../utils/user'
import { useFilesDownloadAll } from '../../../../File'
import {
  AttachServiceRequestsModal,
  useCreateRequest,
} from '../../../../Request'
import { updateNewsboard } from '../../../Newsboard.api'
import { useNewsboardPostProfileContext } from '../../../Newsboard.hooks'
import {
  NEWSBOARD_POST_PROFILE_ACTIONS_WIDGET_ACTION,
  NEWSBOARD_POST_PROFILE_ACTIONS_WIDGET_SERVICE_REQUEST_OPTION,
  NEWSBOARD_POST_SPAM_BLOCK,
  NEWSBOARD_POST_SPAM_UNBLOCK,
} from '../../NewsboardPostProfileActionsWidget.constants'
import { isNewsboardPostProfileActionsWidgetServiceRequestOptionAddNew } from '../../NewsboardPostProfileActionsWidget.utils'

export function useNewsboardPostProfileActionsWidgetHandlers() {
  const { t } = useTranslation()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const user = useUserData()

  const { handleCloseModal, handleEntityReload, postData, setModal } =
    useNewsboardPostProfileContext()

  const { handleClickDownloadAll } = useFilesDownloadAll({
    files: postData?.file_objs || EMPTY_LIST,
    onCloseModal: handleCloseModal,
    onSetModal: setModal,
  })

  const { handleOpenCreation } = useCreateRequest({ setModal })

  function handleError() {
    handleAddErrorNotification('Error.CommonApiRequestFailKey')
  }

  function handlePin() {
    updateNewsboard(postData.uuid, { pinned: !postData.pinned })
      .then(handleEntityReload)
      .catch(handleError)
  }

  function handleBlockPost() {
    const isBlocked = Boolean(postData.spam)

    updateNewsboard(postData.uuid, {
      spam: isBlocked ? NEWSBOARD_POST_SPAM_UNBLOCK : NEWSBOARD_POST_SPAM_BLOCK,
    })
      .then(() => {
        handleAddSuccessNotification(
          isBlocked
            ? 'Notification.NewsboardPostUnblockedKey'
            : 'Notification.NewsboardPostBlockedKey'
        )
        handleEntityReload()
      })
      .catch(handleError)
  }

  function handleBlockUser() {
    const isBlocked = postData.permissions?.can_unblock_owner

    updateUserProfile({
      data: {
        status: isBlocked
          ? USER_STATUS_CODE_IMPORTED
          : USER_STATUS_CODE_BLOCKED,
      },
      id: postData.owner_obj.id,
    })
      .then(() => {
        handleAddSuccessNotification(
          isBlocked
            ? 'Notification.NewsboardPostUserUnblockedKey'
            : 'Notification.NewsboardPostUserBlockedKey'
        )
        handleEntityReload()
      })
      .catch(handleError)
  }

  function handleAttachRequests(requests) {
    updateNewsboard(postData.uuid, { requests: getItemsIds(requests) })
      .then(() => {
        handleCloseModal()
        handleAddSuccessNotification(
          'Notification.NewsboardPostRequestConnectedKey'
        )
        handleEntityReload()
      })
      .catch(() => {
        handleCloseModal()
        handleError()
      })
  }

  function handleOpenAttachModal() {
    setModal(
      <AttachServiceRequestsModal
        allowEmptySelection
        multipleSelection
        headerProps={{ text: 'Modal.AttachRequestsToPostTitleKey' }}
        initialSelection={postData?.request_objs}
        restoreAttention={false}
        onClose={handleCloseModal}
        onSelectedAttachRequests={handleAttachRequests}
      />
    )
  }

  function handleConnectCreatedRequest(request) {
    handleAttachRequests([...(postData?.request_objs || EMPTY_LIST), request])
  }

  function handleCreateRequest() {
    handleOpenCreation({
      formProps: { onSuccess: handleConnectCreatedRequest },
      wizardProps: {
        postUuid: postData?.uuid,
        onSuccess: handleEntityReload,
      },
    })
  }

  function handleSelectServiceRequestOption({ value }) {
    if (isNewsboardPostProfileActionsWidgetServiceRequestOptionAddNew(value)) {
      handleCreateRequest()
    } else {
      handleOpenAttachModal()
    }
  }

  function handleClickAction({ value }) {
    switch (value) {
      case NEWSBOARD_POST_PROFILE_ACTIONS_WIDGET_ACTION.pin:
        handlePin()
        break
      case NEWSBOARD_POST_PROFILE_ACTIONS_WIDGET_ACTION.downloadAllFiles:
        handleClickDownloadAll()
        break
      case NEWSBOARD_POST_PROFILE_ACTIONS_WIDGET_ACTION.blockPost:
        handleBlockPost()
        break
      case NEWSBOARD_POST_PROFILE_ACTIONS_WIDGET_ACTION.blockUser:
        handleBlockUser()
        break
      default:
        break
    }
  }

  const serviceRequestOptions =
    isStaffUser(user) && postData?.permissions?.can_edit
      ? [
          {
            label: t('Newsboard.AddNewRequestActionKey'),
            value:
              NEWSBOARD_POST_PROFILE_ACTIONS_WIDGET_SERVICE_REQUEST_OPTION.addNew,
          },
          {
            label: t('Newsboard.AttachServiceRequestActionOptionKey'),
            value:
              NEWSBOARD_POST_PROFILE_ACTIONS_WIDGET_SERVICE_REQUEST_OPTION.attach,
          },
        ]
      : EMPTY_LIST

  return {
    handleClickAction,
    handleSelectServiceRequestOption,
    serviceRequestOptions,
  }
}
